/**
 * @fileoverview OAuth Callback Handler Page
 * @module OAuthCallbackPage
 * 
 * Business Context:
 * - Handles OAuth callback redirects from providers (Google, etc.)
 * - Processes tokens from URL parameters and stores them
 * - Redirects user to appropriate destination based on auth state
 * 
 * Technical Context:
 * - Receives tokens in URL query parameters from backend OAuth callback
 * - Stores tokens in localStorage via AuthContext
 * - Handles both new and existing user flows
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { consumePostOAuthRedirect } from '../utils/oauth-redirect';

type CallbackState = 'processing' | 'success' | 'error';

export const OAuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUserAndTokens } = useAuth();
  const [state, setState] = useState<CallbackState>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>(
    "You've been signed in successfully. Redirecting to your dashboard..."
  );

  useEffect(() => {
    const handleCallback = async () => {
      // Check for error in URL params
      const error = searchParams.get('error');
      const errorMsg = searchParams.get('message');
      
      if (error) {
        setState('error');
        const authErrorMessage = errorMsg || 'Authentication failed. Please try again.';
        setErrorMessage(authErrorMessage);
        
        // Redirect to auth page after showing error
        setTimeout(() => {
          const postOAuthRedirect = consumePostOAuthRedirect();
          if (postOAuthRedirect?.startsWith('/signup')) {
            navigate(postOAuthRedirect, {
              replace: true,
              state: { oauthError: authErrorMessage },
            });
            return;
          }

          navigate('/auth', {
            replace: true,
            state: { error: authErrorMessage }
          });
        }, 3000);
        return;
      }

      // Get tokens from URL params
      const token = searchParams.get('token');
      const refreshToken = searchParams.get('refreshToken');
      const isNewUser = searchParams.get('isNewUser') === 'true';

      if (!token) {
        setState('error');
        const missingTokenMessage = 'No authentication token received. Please try again.';
        setErrorMessage(missingTokenMessage);
        
        setTimeout(() => {
          const postOAuthRedirect = consumePostOAuthRedirect();
          if (postOAuthRedirect?.startsWith('/signup')) {
            navigate(postOAuthRedirect, {
              replace: true,
              state: { oauthError: missingTokenMessage },
            });
            return;
          }

          navigate('/auth', {
            replace: true,
            state: { error: missingTokenMessage }
          });
        }, 3000);
        return;
      }

      try {
        // Store the token in localStorage
        localStorage.setItem('tbyt_token', token);
        if (refreshToken) {
          localStorage.setItem('tbyt_refresh_token', refreshToken);
        }

        // Fetch user profile and update auth context
        // The setUserAndTokens function will fetch the full profile
        await setUserAndTokens({} as any, token);

        setState('success');
        const postOAuthRedirect = consumePostOAuthRedirect();
        const redirectTarget = postOAuthRedirect || '/dashboard';
        const returningToCheckout = redirectTarget.startsWith('/signup');
        setSuccessMessage(
          returningToCheckout
            ? "You're signed in successfully. Returning you to checkout..."
            : "You've been signed in successfully. Redirecting to your dashboard..."
        );

        // Redirect based on whether user is new or existing
        setTimeout(() => {
          if (returningToCheckout) {
            navigate(redirectTarget, { replace: true });
            return;
          }

          if (isNewUser) {
            // New users might need property setup
            // Check if they have properties, if not redirect to setup
            navigate('/dashboard', { replace: true });
            return;
          }

          // Existing users go directly to dashboard
          navigate('/dashboard', { replace: true });
        }, returningToCheckout ? 750 : 1500);

      } catch (err) {
        console.error('OAuth callback error:', err);
        setState('error');
        const callbackErrorMessage = 'Failed to complete authentication. Please try again.';
        setErrorMessage(callbackErrorMessage);
        
        // Clear any partial auth state
        localStorage.removeItem('tbyt_token');
        localStorage.removeItem('tbyt_refresh_token');
        
        setTimeout(() => {
          const postOAuthRedirect = consumePostOAuthRedirect();
          if (postOAuthRedirect?.startsWith('/signup')) {
            navigate(postOAuthRedirect, {
              replace: true,
              state: { oauthError: callbackErrorMessage },
            });
            return;
          }

          navigate('/auth', {
            replace: true,
            state: { error: callbackErrorMessage }
          });
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setUserAndTokens]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-white flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-8 lg:p-12 max-w-md w-full mx-4 text-center">
        {state === 'processing' && (
          <>
            <Loader2 className="w-16 h-16 text-teal-500 mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Completing Sign In
            </h1>
            <p className="text-slate-600">
              Please wait while we verify your account...
            </p>
          </>
        )}

        {state === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Welcome!
            </h1>
            <p className="text-slate-600">
              {successMessage}
            </p>
          </>
        )}

        {state === 'error' && (
          <>
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Sign In Failed
            </h1>
            <p className="text-slate-600 mb-4">
              {errorMessage}
            </p>
            <button
              onClick={() => navigate('/auth')}
              className="w-full py-3 px-4 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors"
            >
              Back to Sign In
            </button>
          </>
        )}
      </div>
    </div>
  );
};



